import { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  KeyRound, MailboxIcon, ParkingSquare, PawPrint, Receipt,
  DoorOpen, User, RotateCcw, PlayCircle, AlertCircle, CheckCircle2, DollarSign,
} from "lucide-react";

/**
 * MoveOutWizardPage — mirror of MoveInChecklistPage. One admin click revokes
 * every fob, returns mailbox keys, releases parking, closes pet registrations,
 * and generates a security-deposit refund draft with itemized deductions.
 */

const REASONS = [
  { value: "moving_out", label: "Moving out (voluntary)" },
  { value: "lease_end", label: "Lease ended (non-renewal)" },
  { value: "eviction", label: "Eviction" },
  { value: "other", label: "Other" },
];

const DEFAULT_FORM = () => ({
  resident_id: "",
  reason: "moving_out",
  notes: "",
  steps: {
    revoke_access: true,
    return_mailbox_keys: true,
    release_parking: true,
    close_pets: true,
  },
  deposit: {
    enabled: true,
    deposit_paid: 0,
    cleaning_charges: 0,
    damages: 0,
    other_deductions: 0,
    other_deductions_memo: "",
    forwarding_address: "",
    notes: "",
  },
});

export default function MoveOutWizardPage() {
  const { propertyId } = useProperty();
  const [form, setForm] = useState(DEFAULT_FORM());
  const [residents, setResidents] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      try {
        const [r, h] = await Promise.all([
          api.get(`/admin/residents?property_id=${propertyId}`),
          api.get(`/admin/move-out/history?property_id=${propertyId}&limit=8`).catch(() => ({ data: { items: [] } })),
        ]);
        setResidents(r.data.items || []);
        setHistory(h.data.items || []);
      } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/MoveOutWizardPage.jsx: swallowed', err); }
    })();
  }, [propertyId, result]);

  const pickResident = async (rid) => {
    setForm((f) => ({ ...f, resident_id: rid }));
    setPreview(null);
    if (!rid) return;
    try {
      const { data } = await api.get(`/admin/move-out/preview/${rid}?property_id=${propertyId}`);
      setPreview(data);
      setForm((f) => ({
        ...f,
        deposit: { ...f.deposit, deposit_paid: data.deposit_paid_on_lease || 0 },
      }));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const toggleStep = (k, v) => setForm((f) => ({ ...f, steps: { ...f.steps, [k]: v } }));
  const setDep = (k, v) => setForm((f) => ({ ...f, deposit: { ...f.deposit, [k]: v } }));
  const reset = () => { setForm(DEFAULT_FORM()); setPreview(null); setResult(null); };

  const refundAmount = useMemo(() => {
    const d = form.deposit;
    const deductions = Number(d.cleaning_charges || 0) + Number(d.damages || 0) + Number(d.other_deductions || 0);
    return Math.max(0, Number(d.deposit_paid || 0) - deductions).toFixed(2);
  }, [form.deposit]);

  const canRun = useMemo(() => {
    if (!form.resident_id) return false;
    // At least one step or the deposit refund
    const anyStep = Object.values(form.steps).some(Boolean);
    return anyStep || form.deposit.enabled;
  }, [form]);

  const run = async () => {
    if (!canRun) return toast.error("Pick a resident + at least one action");
    if (!window.confirm("This will DEACTIVATE the resident's login and revoke every fob/mailbox/parking assignment for them. Continue?")) return;
    setBusy(true);
    try {
      const payload = {
        property_id: propertyId,
        resident_id: form.resident_id,
        reason: form.reason,
        notes: form.notes || null,
        revoke_access: form.steps.revoke_access,
        return_mailbox_keys: form.steps.return_mailbox_keys,
        release_parking: form.steps.release_parking,
        close_pets: form.steps.close_pets,
        deposit_refund: form.deposit.enabled ? {
          enabled: true,
          deposit_paid: Number(form.deposit.deposit_paid || 0),
          cleaning_charges: Number(form.deposit.cleaning_charges || 0),
          damages: Number(form.deposit.damages || 0),
          other_deductions: Number(form.deposit.other_deductions || 0),
          other_deductions_memo: form.deposit.other_deductions_memo || null,
          forwarding_address: form.deposit.forwarding_address || null,
          notes: form.deposit.notes || null,
        } : null,
      };
      const { data } = await api.post("/admin/move-out/wizard", payload);
      setResult(data);
      toast.success(`Move-out complete — ${data.summary?.completed || 0}/${data.summary?.attempted || 0} actions landed`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Move-out Wizard"
        subtitle="Mirror of Move-in. One click revokes every fob, returns keys, releases parking, closes pet registrations, and drafts the security-deposit refund. Every touched row shares a move_out_id for audit."
        actions={
          <div className="flex items-center gap-2">
            {result && (
              <Button variant="outline" onClick={reset} className="gap-2 border-white/10" data-testid="moveout-reset">
                <RotateCcw className="h-4 w-4" /> New move-out
              </Button>
            )}
            <Button onClick={run} disabled={!canRun || busy}
                    className="bg-rose-500 hover:bg-rose-600 gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="moveout-run">
              <PlayCircle className="h-4 w-4" />
              {busy ? "Running…" : "Complete move-out"}
            </Button>
          </div>
        }
      />

      {result && <ResultSummary result={result} />}

      {!result && (
        <>
          <Card className="border-white/10 bg-card p-5 mb-4" data-testid="moveout-resident-card">
            <div className="flex items-center gap-2 mb-3">
              <User className="h-4 w-4 text-rose-300" />
              <div className="font-semibold text-sm">Departing resident</div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Resident *</Label>
                <Select value={form.resident_id} onValueChange={pickResident}>
                  <SelectTrigger data-testid="moveout-resident-picker" className="bg-white/[0.02] border-white/10">
                    <SelectValue placeholder="Pick the resident who is moving out" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {residents.filter((r) => r.active !== false).map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name} · Unit {r.unit || "—"} · {r.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Reason</Label>
                <Select value={form.reason} onValueChange={(v) => setForm((f) => ({ ...f, reason: v }))}>
                  <SelectTrigger data-testid="moveout-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea rows={2} value={form.notes}
                          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                          placeholder="Anything ops should know for the deposit refund review" />
              </div>
            </div>

            {preview && (
              <div className="mt-4 rounded-md border border-white/10 bg-black/20 p-3" data-testid="moveout-preview">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Current footprint</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[12px]">
                  <FootStat icon={KeyRound} label="Active fobs" value={preview.footprint.active_access_credentials} accent="text-orange-300" />
                  <FootStat icon={MailboxIcon} label="Mailbox" value={preview.footprint.mailbox_assignments} accent="text-sky-300" />
                  <FootStat icon={ParkingSquare} label="Parking" value={preview.footprint.parking_spots} accent="text-emerald-300" />
                  <FootStat icon={PawPrint} label="Active pets" value={preview.footprint.active_pets} accent="text-amber-300" />
                </div>
              </div>
            )}
          </Card>

          {form.resident_id && (
            <>
              <Card className="border-white/10 bg-card p-4 mb-3">
                <div className="text-sm font-semibold mb-3">Actions to perform</div>
                <div className="grid md:grid-cols-2 gap-2">
                  <StepRow k="revoke_access" label="Revoke all access credentials" icon={KeyRound}
                            enabled={form.steps.revoke_access} onToggle={(v) => toggleStep("revoke_access", v)} />
                  <StepRow k="return_mailbox_keys" label="Return mailbox keys" icon={MailboxIcon}
                            enabled={form.steps.return_mailbox_keys} onToggle={(v) => toggleStep("return_mailbox_keys", v)} />
                  <StepRow k="release_parking" label="Release parking spots" icon={ParkingSquare}
                            enabled={form.steps.release_parking} onToggle={(v) => toggleStep("release_parking", v)} />
                  <StepRow k="close_pets" label="Close pet registrations" icon={PawPrint}
                            enabled={form.steps.close_pets} onToggle={(v) => toggleStep("close_pets", v)} />
                </div>
              </Card>

              <Card className={"border-white/10 bg-card p-5 mb-3 transition-opacity " +
                                (form.deposit.enabled ? "" : "opacity-50")}
                     data-testid="moveout-deposit-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-fuchsia-300" />
                    <div className="font-semibold text-sm">Security-deposit refund draft</div>
                  </div>
                  <Switch checked={form.deposit.enabled}
                          onCheckedChange={(v) => setDep("enabled", v)}
                          data-testid="moveout-deposit-toggle" />
                </div>
                {form.deposit.enabled && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs">Deposit paid ($)</Label>
                        <Input type="number" step="0.01" min="0" value={form.deposit.deposit_paid}
                               onChange={(e) => setDep("deposit_paid", e.target.value)}
                               data-testid="moveout-deposit-paid" />
                      </div>
                      <div>
                        <Label className="text-xs">Cleaning ($)</Label>
                        <Input type="number" step="0.01" min="0" value={form.deposit.cleaning_charges}
                               onChange={(e) => setDep("cleaning_charges", e.target.value)}
                               data-testid="moveout-cleaning" />
                      </div>
                      <div>
                        <Label className="text-xs">Damages ($)</Label>
                        <Input type="number" step="0.01" min="0" value={form.deposit.damages}
                               onChange={(e) => setDep("damages", e.target.value)}
                               data-testid="moveout-damages" />
                      </div>
                      <div>
                        <Label className="text-xs">Other ($)</Label>
                        <Input type="number" step="0.01" min="0" value={form.deposit.other_deductions}
                               onChange={(e) => setDep("other_deductions", e.target.value)}
                               data-testid="moveout-other" />
                      </div>
                    </div>
                    {Number(form.deposit.other_deductions) > 0 && (
                      <div>
                        <Label className="text-xs">Other deductions memo</Label>
                        <Input value={form.deposit.other_deductions_memo}
                               onChange={(e) => setDep("other_deductions_memo", e.target.value)}
                               placeholder="Late fees, unpaid utilities, etc." />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs">Forwarding address for refund check</Label>
                      <Input value={form.deposit.forwarding_address}
                             onChange={(e) => setDep("forwarding_address", e.target.value)}
                             placeholder="123 New Home St, Anytown, FL 33401"
                             data-testid="moveout-forwarding" />
                    </div>
                    <div className="flex items-center justify-between rounded-md border border-emerald-500/30 bg-emerald-500/[0.06] p-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-emerald-300">Refund amount</div>
                        <div className="text-xs text-zinc-400">= Deposit − (Cleaning + Damages + Other)</div>
                      </div>
                      <div className="text-2xl font-bold text-emerald-300 flex items-center gap-1" data-testid="moveout-refund-amount">
                        <DollarSign className="h-5 w-5" /> {refundAmount}
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            </>
          )}

          {history.length > 0 && (
            <Card className="border-white/10 bg-card p-4 mt-6" data-testid="moveout-history">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Recent move-outs ({history.length})
              </div>
              <div className="space-y-2 text-[12px]">
                {history.map((h) => (
                  <div key={h.move_out_id} className="flex items-center justify-between border border-white/5 rounded-md px-2 py-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{h.resident_name} · Unit {h.unit}</div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {h.created_at ? new Date(h.created_at).toLocaleString() : ""}
                        {h.refund_amount != null && ` · refund $${h.refund_amount}`}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {Object.entries(h.item_counts || {}).filter(([, n]) => n > 0).map(([k, n]) => (
                        <Badge key={k} variant="outline" className="text-[10px] border-white/10">
                          {k} · {n}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function FootStat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.02] p-2">
      <div className="flex items-center gap-1.5">
        <Icon className={"h-3.5 w-3.5 " + accent} />
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      </div>
      <div className={"text-xl font-bold mt-0.5 " + (value > 0 ? accent : "text-zinc-500")}>{value}</div>
    </div>
  );
}

function StepRow({ k, label, icon: Icon, enabled, onToggle }) {
  return (
    <div className={"flex items-center justify-between rounded-md border p-2 transition-colors " +
                     (enabled ? "border-white/10 bg-white/[0.02]" : "border-white/5 opacity-50")}
         data-testid={`moveout-step-${k}`}>
      <div className="flex items-center gap-2 text-[13px]">
        <Icon className="h-4 w-4 text-rose-300" />
        <span>{label}</span>
      </div>
      <Switch checked={enabled} onCheckedChange={onToggle} data-testid={`moveout-step-toggle-${k}`} />
    </div>
  );
}

function ResultSummary({ result }) {
  const steps = result.steps || {};
  const refund = steps.deposit_refund;
  return (
    <Card className="border-emerald-500/30 bg-emerald-500/[0.02] p-5 mb-4" data-testid="moveout-result">
      <div className="flex items-center gap-2 mb-3">
        <DoorOpen className="h-5 w-5 text-emerald-400" />
        <div className="font-semibold">Move-out complete</div>
        <Badge variant="outline" className="border-emerald-500/40 text-emerald-300">
          {result.summary?.completed || 0} of {result.summary?.attempted || 0} landed
        </Badge>
      </div>
      <div className="text-[12px] text-zinc-400 mb-3">
        Move-out ID <span className="font-mono">{result.move_out_id}</span> · Resident{" "}
        <span className="font-semibold text-white">{result.resident?.name}</span> deactivated · Reason: {result.reason}
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        {Object.entries(steps).map(([k, s]) => (
          <StepResult key={k} k={k} s={s} />
        ))}
      </div>
      {refund && refund.ok && (
        <div className="mt-4 rounded-md border border-fuchsia-500/30 bg-fuchsia-500/[0.06] p-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-300">Deposit refund draft</div>
            <div className="text-xs text-zinc-400">Status: draft · Deductions: ${refund.total_deductions}</div>
          </div>
          <div className="text-2xl font-bold text-fuchsia-300 flex items-center gap-1">
            <DollarSign className="h-5 w-5" /> {refund.refund_amount}
          </div>
        </div>
      )}
    </Card>
  );
}

function StepResult({ k, s }) {
  const labels = {
    access: "Access credentials revoked",
    mailbox: "Mailbox keys returned",
    parking: "Parking released",
    pets: "Pets closed",
    deposit_refund: "Refund draft",
  };
  const details = () => {
    if (k === "access") return `${s.revoked_count || 0} credential(s)`;
    if (k === "mailbox") return `${s.returned_count || 0} box(es)`;
    if (k === "parking") return `${s.released_count || 0} spot(s)`;
    if (k === "pets") return `${s.closed_count || 0} pet(s)`;
    if (k === "deposit_refund") return `$${s.refund_amount || 0}`;
    return "";
  };
  return (
    <div className={"flex items-start gap-2 border rounded-md p-2 " +
      (s.ok ? "border-emerald-500/30 bg-emerald-500/[0.04]"
            : "border-amber-500/30 bg-amber-500/[0.04]")}
         data-testid={`moveout-result-${k}`}>
      {s.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
            : <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-medium">{labels[k] || k}</div>
        <div className="text-[11px] text-zinc-500">
          {s.ok ? details() : s.error || "Skipped"}
        </div>
      </div>
    </div>
  );
}
